import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { filter, head, isEmpty } from 'lodash';
import moment from 'moment';
import { combineLatest, forkJoin, iif, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';

import {
    CalendarDateObject,
    CalendarTypeModel,
} from '@components/report-filters/filters/calendars-filter/models/calendarTypeModel';
import { DateSourceModel } from '@components/report-filters/filters/calendars-filter/models/dateSourceModel';
import { prepareSitesForRequest } from '@components/report-filters/utils/filter.utils';
import { BUSINESS_DATE_FORMAT } from '@config/global-constants';
import { set_selected_date } from '@rpt-ngrx/actions/filters.action';
import { selectEventState } from '@rpt-ngrx/selectors/events.selector';
import { selectDateForMultipleDate, selectStoredDates } from '@rpt-ngrx/selectors/filters.selector';
import { selectSelectedData } from '@rpt-ngrx/selectors/select.selector';
import { selectSiteState } from '@rpt-ngrx/selectors/sites.selector';
import { FiltersService } from '@services/filters';
import { ReportsService } from '@shared/services';
import { IEvent, IOpenReportHistory, ISite } from '@shared/types';
import { IEventState } from '@shared/types/event';
import { ISiteState } from '@shared/types/site';
import { getMaxDate, getMinDate } from '@shared/utils';
import { XUX_DATEPICKER_PERIODS } from '@xenial-ux/xux-datepicker/datepicker-specific-constants';
import { IDatepickerValue, IPeriod } from '@xenial-ux/xux-datepicker/types';
import { DateTypeModel } from '@xenial-ux/xux-datepicker/types/dateTypeModel';

export type ServiceResponse = CalendarDateObject & {
    limit?: number;
    limitMinDate?: number;
    limitMaxDate?: number;
};

@Injectable({
    providedIn: 'root',
})
export class CalendarPickerService {
    private filtersState$: Observable<{
        selectedDateForMultiDate: IDatepickerValue;
        selectedDateForSingleDate: IDatepickerValue;
    }>;
    private siteState$: Observable<ISiteState>;
    private eventsState$: Observable<IEventState>;

    public dependencyMap = {
        Site_Id: 'getDateBySites',
        Event_Id: 'getDateByEvents',
        None: 'getDateByCompany',
        Company_Id: 'getDateByCompanyDependencies',
        Event_Id_Hide: 'hideCalendarWhenEventSelected',
        Report_Type: 'getDayLimitByReportType',
    };

    constructor(
        private reportService: ReportsService,
        private store: Store,
        private filtersService: FiltersService,
    ) {
        this.filtersState$ = this.store.select(selectStoredDates);
        this.siteState$ = this.store.select(selectSiteState);
        this.eventsState$ = this.store.select(selectEventState);
    }

    public getDayLimitByReportType({ limitations }: Record<string, unknown>): Observable<{
        limit?: number;
        limitMinDate?: number;
        limitMaxDate?: number;
    } | null> {
        return this.store.select(selectSelectedData).pipe(
            map((data) => {
                if (!data || !limitations) {
                    return null;
                }
                const reportType = data['Report_Type'];

                if (!reportType) {
                    return null;
                }
                const dependencyMap = limitations['Limit_Day_Selection_Dependency'];

                if (!dependencyMap) {
                    return null;
                }
                const limitConfig = dependencyMap[reportType];
                return typeof limitConfig === 'undefined' ? null : limitConfig;
            }),
        );
    }

    private filterSites(limitations: Record<string, string | unknown>, sites: ISite[]): ISite[] {
        if (!limitations || !limitations['Limit_Site_Selection_Condition']) {
            return sites;
        }

        if (limitations['Limit_Site_Selection_Condition'] === 'Sites_With_Uber_Subscription') {
            return sites.filter(
                (site) =>
                    !!site.subscriptions_data?.some(
                        (subscription) =>
                            subscription.is_active &&
                            subscription.external_data.delivery_services?.some(
                                (service) => service.service_name === 'UberEats' && service.service_enabled,
                            ),
                    ),
            );
        }
        return sites;
    }

    public getDateBySites({
        multipleSites,
        multipleDates,
        typeOfCalendar,
        business_date_category,
        default_date,
        limitations,
    }) {
        return this.getSites(multipleSites, limitations).pipe(
            switchMap(({ allSites, siteId }) => {
                return this.getCalendarsAndBusinessDate(
                    allSites,
                    siteId,
                    multipleDates,
                    typeOfCalendar,
                    business_date_category,
                    default_date,
                );
            }),
        );
    }

    public getDateByCompanyDependencies({
        multipleEvents,
        multipleDates,
        typeOfCalendar,
        business_date_category,
        default_date,
        use_ordering_date = false,
        min_date = null,
        enable_min_date_check = false,
        limitations,
    }) {
        if (!use_ordering_date) {
            return this.getDateByCompany({
                multipleDates,
                typeOfCalendar,
                business_date_category,
                default_date,
                limitations,
            });
        }

        return combineLatest([
            this.getDateByCompany({
                multipleDates,
                typeOfCalendar,
                business_date_category,
                default_date,
                limitations,
            }),
            this.getDateByEvents({
                multipleEvents,
                use_ordering_date,
                min_date,
                enable_min_date_check,
            }),
        ]).pipe(
            map(([companyDateResult, eventDateResult]) => {
                if (use_ordering_date) {
                    const { inputCalendarValue } = eventDateResult;
                    if (!inputCalendarValue) {
                        return companyDateResult;
                    }
                    return eventDateResult;
                }
                return companyDateResult;
            }),
        );
    }

    public getDateByCompany({ multipleDates, typeOfCalendar, business_date_category, default_date, limitations }) {
        return this.getSites(false, limitations).pipe(
            switchMap(({ allSites }) =>
                this.getCalendarsAndBusinessDate(
                    allSites,
                    [],
                    multipleDates,
                    typeOfCalendar,
                    business_date_category,
                    default_date,
                ),
            ),
        );
    }

    private getSites(multipleSites, limitations): Observable<{ allSites: Array<ISite>; siteId: Array<string> }> {
        return this.siteState$.pipe(
            distinctUntilChanged((prev, next) =>
                this.checkIfStoredDataChanged(
                    multipleSites,
                    multipleSites ? prev.selectedSites : prev.selectedSite,
                    multipleSites ? next.selectedSites : next.selectedSite,
                ),
            ),
            map((siteState) => {
                const allSites = siteState.sites;
                let siteId = null;
                if (multipleSites && siteState.selectedSites) {
                    siteId = prepareSitesForRequest(this.filterSites(limitations, siteState.selectedSites));
                }
                if (!multipleSites && siteState.selectedSite) {
                    siteId = prepareSitesForRequest([siteState.selectedSite]);
                }

                return {
                    allSites,
                    siteId,
                };
            }),
        );
    }

    private getCalendarsAndBusinessDate(
        allSites: ISite[],
        siteId: Array<string>,
        multipleDates: boolean,
        typeOfCalendar: CalendarTypeModel,
        business_date_category: string,
        default_date: string,
    ): Observable<CalendarDateObject> {
        if (!multipleDates) {
            return this.reportService.fetchBusinessDate(business_date_category, typeOfCalendar, siteId, allSites).pipe(
                take(1),
                map((businessDate) => ({
                    businessDate: businessDate['business_date'],
                    calendars: [],
                })),
                switchMap(({ businessDate, calendars }) =>
                    this.getDateFromState(multipleDates, businessDate, calendars, default_date),
                ),
            );
        } else {
            return forkJoin([
                this.reportService.fetchCalendars(siteId, typeOfCalendar),
                this.reportService.fetchBusinessDate(business_date_category, typeOfCalendar, siteId, allSites),
            ]).pipe(
                map(([calendars, businessDate]) => ({
                    businessDate: businessDate['business_date'],
                    calendars: [...calendars],
                })),
                switchMap(({ businessDate, calendars }) =>
                    this.getDateFromState(multipleDates, businessDate, calendars, default_date),
                ),
            );
        }
    }

    private getDateFromState(multipleDates, businessDate, calendars, default_date): Observable<CalendarDateObject> {
        return this.filtersState$.pipe(
            distinctUntilChanged((prev, next) =>
                this.checkIfStoredDataChanged(
                    multipleDates,
                    multipleDates ? prev.selectedDateForMultiDate : prev.selectedDateForSingleDate,
                    multipleDates ? next.selectedDateForMultiDate : next.selectedDateForSingleDate,
                ),
            ),
            map(({ selectedDateForSingleDate, selectedDateForMultiDate }) => {
                const inputCalendarValue = multipleDates
                    ? selectedDateForMultiDate || {
                          value: null,
                          type: default_date,
                      }
                    : selectedDateForSingleDate || {
                          value: {
                              dateStart: moment(businessDate, BUSINESS_DATE_FORMAT).toDate(),
                              dateEnd: moment(businessDate, BUSINESS_DATE_FORMAT).toDate(),
                          },
                          type: default_date,
                      };
                return {
                    inputCalendarValue,
                    calendars,
                    businessDate,
                    source: DateSourceModel.STATE,
                };
            }),
        );
    }

    public getDateByEvents({
        multipleEvents,
        use_ordering_date = false,
        use_start_time = false,
        min_date = null,
        enable_min_date_check = false,
    }): Observable<CalendarDateObject> {
        const checkMinDate = enable_min_date_check && !isEmpty(min_date);

        return this.eventsState$.pipe(
            distinctUntilChanged((prev, next) =>
                this.checkIfStoredDataChanged(
                    multipleEvents,
                    multipleEvents ? prev.selectedEvents : prev.selectedEvent,
                    multipleEvents ? next.selectedEvents : next.selectedEvent,
                ),
            ),
            map(({ selectedEvents, selectedEvent }) => ({ selectedEvents, selectedEvent })),
            switchMap(({ selectedEvents, selectedEvent }) =>
                iif(
                    () => (multipleEvents ? selectedEvents.length !== 0 : !!selectedEvent),
                    of(true).pipe(
                        map(() => {
                            const events = multipleEvents ? selectedEvents : [selectedEvent];
                            const filteredEvents = checkMinDate
                                ? filter(events, (event) =>
                                      moment(min_date).isSameOrBefore(moment(head(event.event_days)?.start_date_time)),
                                  )
                                : events;
                            const value = this.getEventDateRange(filteredEvents, use_ordering_date, use_start_time);
                            const inputCalendarValue = {
                                value,
                                type: DateTypeModel.CUSTOM,
                            };

                            return {
                                inputCalendarValue,
                                calendars: [],
                                businessDate: null,
                                source: DateSourceModel.EVENT,
                            };
                        }),
                    ),
                    of({
                        inputCalendarValue: null,
                        calendars: [],
                        businessDate: null,
                        source: DateSourceModel.EVENT,
                    }),
                ),
            ),
        );
    }

    private checkIfStoredDataChanged(multi: boolean, prevData: any, nextData: any): boolean {
        if (multi) {
            return JSON.stringify(prevData) === JSON.stringify(nextData);
        } else {
            if (!prevData) {
                return false;
            }
            return JSON.stringify(prevData) === JSON.stringify(nextData);
        }
    }

    private getEventDateRange(selectedEvents: IEvent[], use_ordering_date: boolean, use_start_time: boolean) {
        const maxDate = getMaxDate(
            selectedEvents.reduce((acc, event) => acc.concat(event.event_days.map((day) => day.end_date_time)), []),
        );
        const minDate = getMinDate(
            selectedEvents.reduce((acc, event) => acc.concat(event.event_days.map((day) => day.start_date_time)), []),
        );

        const order_dates = selectedEvents.reduce(
            (acc, event) => acc.concat((event.order_dates || []).map((day) => day)),
            [],
        );
        const maxOrderDate = getMaxDate(order_dates);
        const minOrderDate = getMinDate(order_dates);

        if (use_ordering_date) {
            return {
                dateStart: order_dates.length ? (minOrderDate.format() as unknown as Date) : '',
                dateEnd: order_dates.length ? (maxOrderDate.format() as unknown as Date) : '',
            };
        }

        if (use_start_time) {
            const maxStartDate = getMaxDate(
                selectedEvents.reduce(
                    (acc, event) => acc.concat(event.event_days.map((day) => day.start_date_time)),
                    [],
                ),
            );
            const minStartDate = getMinDate(
                selectedEvents.reduce(
                    (acc, event) => acc.concat(event.event_days.map((day) => day.start_date_time)),
                    [],
                ),
            );

            return {
                dateStart: minStartDate.format() as unknown as Date,
                dateEnd: maxStartDate.format() as unknown as Date,
            };
        }

        return {
            dateStart: minDate.subtract(1, 'd') as unknown as Date,
            dateEnd: maxDate.add(1, 'd') as unknown as Date,
        };
    }

    public getSelectedDate(): Observable<{
        selectedDateForMultiDate: IDatepickerValue;
        calendars: Array<IPeriod>;
        businessDate: string;
    }> {
        return this.store.select(selectDateForMultipleDate).pipe(
            take(1),
            map(({ selectedDateForMultiDate, calendars, businessDate }) => ({
                selectedDateForMultiDate,
                calendars,
                businessDate,
            })),
        );
    }

    public getDashboardCalendarOptions(businessDate: string, calendars: Array<IPeriod>, defaultDateType: string) {
        XUX_DATEPICKER_PERIODS.LATEST_BUSINESS_DATE = {
            ...XUX_DATEPICKER_PERIODS.LATEST_BUSINESS_DATE,
            value: {
                dateStart: moment(businessDate).toDate(),
                dateEnd: moment(businessDate).toDate(),
            },
        };
        XUX_DATEPICKER_PERIODS.PERIODS = {
            ...XUX_DATEPICKER_PERIODS.PERIODS,
            value: {
                ...this.filtersService.getDefaultPeriod(calendars, defaultDateType, null),
            },
        };

        return !calendars.length
            ? [
                  XUX_DATEPICKER_PERIODS.LATEST_BUSINESS_DATE,
                  XUX_DATEPICKER_PERIODS.PERIODS_MONTH,
                  XUX_DATEPICKER_PERIODS.PERIODS_YEAR,
                  XUX_DATEPICKER_PERIODS.CUSTOM,
              ]
            : [
                  XUX_DATEPICKER_PERIODS.PERIODS,
                  XUX_DATEPICKER_PERIODS.LATEST_BUSINESS_DATE,
                  XUX_DATEPICKER_PERIODS.PERIODS_MONTH,
                  XUX_DATEPICKER_PERIODS.PERIODS_YEAR,
                  XUX_DATEPICKER_PERIODS.CUSTOM,
              ];
    }

    private hideCalendarWhenEventSelected({ multipleEvents }) {
        return this.eventsState$.pipe(
            take(1),
            distinctUntilChanged((prev, next) =>
                this.checkIfStoredDataChanged(
                    multipleEvents,
                    multipleEvents ? prev.selectedEvents : prev.selectedEvent,
                    multipleEvents ? next.selectedEvents : next.selectedEvent,
                ),
            ),
            map(({ selectedEvents, selectedEvent }) => {
                return {
                    hide: !isEmpty(multipleEvents ? selectedEvents : selectedEvent),
                };
            }),
        );
    }

    public resetDateMainFilter(history: IOpenReportHistory) {
        if (!history.filters) {
            return;
        }
        this.store.dispatch(
            set_selected_date({
                typeOfCalendar: CalendarTypeModel.FISC,
                selectedDate: {
                    type: 'custom',
                    value: {
                        dateStart: moment(history.filters.Start_Date).toDate(),
                        dateEnd: moment(history.filters.End_Date).toDate(),
                    },
                },
                applied: true,
            }),
        );
    }
}
